import { DownloadIcon, MailIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, tone } from '../../primitives';

/**
 * The Leads page, with the answers to a qualifying question in their own
 * column, and the email the team gets for each new lead.
 */
export default function LeadsBoard() {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  const leads = [
    {
      name: 'Anna Kowalska',
      email: 'anna@example.com',
      size: '25–50',
      date: t('Today'),
      fresh: true,
    },
    {
      name: 'Diego Ramos',
      email: 'diego@example.com',
      size: '10–25',
      date: t('Today'),
    },
    {
      name: 'Mei Lin',
      email: 'mei@example.com',
      size: '50+',
      date: t('Yesterday'),
    },
    {
      name: 'Tom Becker',
      email: 'tom@example.com',
      size: '5–10',
      date: t('Yesterday'),
    },
  ];

  return (
    <div>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center justify-between gap-4 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            {tc('Leads')}
          </p>
          <span
            className={clsxm(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
              tone.line,
              tone.ink,
            )}
          >
            <DownloadIcon className='h-3.5 w-3.5' />
            {t('Export CSV')}
          </span>
        </div>

        <table className='w-full text-start text-[13px]'>
          <thead>
            <tr className={tone.quiet}>
              <th className='px-5 pb-2 pt-4 text-start font-medium'>
                {tc('Name')}
              </th>
              <th className='hidden px-3 pb-2 pt-4 text-start font-medium sm:table-cell'>
                {tc('Email')}
              </th>
              <th className='px-3 pb-2 pt-4 text-start font-medium'>
                {t('Team size')}
              </th>
              <th className='px-5 pb-2 pt-4 text-end font-medium'>
                {t('Date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {leads.map(({ name, email, size, date, fresh }, index) => (
              <tr
                key={name}
                className={clsxm(
                  styles.rv,
                  'border-t border-black/[0.05] dark:border-white/[0.05]',
                  fresh && 'bg-primary-50/60 dark:bg-primary-400/[0.07]',
                )}
                style={delay(150 + index * 90)}
              >
                <td className='px-5 py-3'>
                  <span
                    className={clsxm(
                      'flex items-center gap-2 font-medium',
                      tone.ink,
                    )}
                  >
                    {name}
                    {fresh && (
                      <span className='rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
                        {t('New')}
                      </span>
                    )}
                  </span>
                </td>
                <td
                  className={clsxm('hidden px-3 py-3 sm:table-cell', tone.body)}
                  dir='ltr'
                >
                  {email}
                </td>
                <td className={clsxm('px-3 py-3 tabular-nums', tone.body)}>
                  {size}
                </td>
                <td className={clsxm('px-5 py-3 text-end', tone.quiet)}>
                  {date}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='h-3' />
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-start gap-3 rounded-2xl p-4 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:-mt-5 sm:ms-6 sm:w-[19rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(700)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
          <MailIcon className='h-4 w-4' />
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {tc('New lead')} · Anna Kowalska
          </span>
          <span className={clsxm('mt-0.5 block text-xs leading-5', tone.body)}>
            {t('Team of 25–50, wants a demo before the end of the quarter.')}
          </span>
        </span>
      </div>
    </div>
  );
}
